/**
 * CustomerInvoicesPage, a customer's Invoices tab, `/customers/:customerId/invoices`
 * (docs/features/order-to-invoice/50_finance_sales_ledger_module_spec.md §9
 * item 4): its pro-formas, invoices and credit notes from the sales ledger
 * (CIS documents only; Tharstern's invoices stay in Tharstern), credit notes
 * negative so a column adds up, each opening its own page. The customer's id is
 * its account code.
 *
 * No mock-up was drawn for this tab: it is the smallest list consistent with
 * Finance › Awaiting payment and the customer's other tabs. Search, filter,
 * sort and paging run on the server (`listCustomerDocuments`,
 * docs/list-endpoints.md); this page holds them in the URL, so a filtered list
 * is a link. Changing any of them goes back to page 1.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useCustomerRouteId } from "../app/components/domain/customers/useCustomerRouteId";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ListResponse } from "@shared-core/data/adapter";
import type {
  CustomerDocumentItem,
  CustomerDocumentKind,
  CustomerDocumentState,
} from "@shared-core/data/sales-ledger-types";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { CustomerFrame } from "../app/components/domain/customers/CustomerFrame";
import {
  StateBadge,
  usePdfDownload,
} from "../app/components/domain/sales-ledger/DocumentParts";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { cn } from "../app/components/primitives/utils";

const PAGE_SIZE = 25;
const selectClass = "h-9 rounded-md border border-border bg-card px-3 text-sm";

const TYPE_OPTIONS: { value: CustomerDocumentKind; label: string }[] = [
  { value: "proforma", label: "Pro-formas" },
  { value: "invoice", label: "Invoices" },
  { value: "credit_note", label: "Credit notes" },
];
/** The filter's own nouns; a row says its state in the backend's words. */
const STATE_OPTIONS: { value: CustomerDocumentState; label: string }[] = [
  { value: "issued", label: "Issued" },
  { value: "held", label: "Held" },
  { value: "draft_in_xero", label: "Draft in Xero" },
  { value: "approved_in_xero", label: "Approved in Xero" },
  { value: "awaiting_payment", label: "Awaiting payment" },
  { value: "part_paid", label: "Part paid" },
  { value: "paid", label: "Paid" },
  { value: "applied", label: "Applied" },
  { value: "refund_pending", label: "Refund pending" },
  { value: "refund_failed", label: "Refund failed" },
  { value: "refunded", label: "Refunded" },
  { value: "superseded", label: "Replaced" },
  { value: "expired", label: "Expired" },
  { value: "voided", label: "Voided" },
  { value: "voided_in_xero", label: "Voided in Xero" },
];
const SORT_OPTIONS = [
  { value: "-issued_at", label: "Newest first" },
  { value: "issued_at", label: "Oldest first" },
  { value: "document_no", label: "Document number" },
  { value: "-gross_pence", label: "Largest first" },
] as const;
type SortValue = (typeof SORT_OPTIONS)[number]["value"];

function readFilters(params: URLSearchParams) {
  const type = params.get("type");
  const state = params.get("state");
  const sort = params.get("sort");
  return {
    type: TYPE_OPTIONS.some((o) => o.value === type)
      ? (type as CustomerDocumentKind)
      : undefined,
    state: STATE_OPTIONS.some((o) => o.value === state)
      ? (state as CustomerDocumentState)
      : undefined,
    sort: SORT_OPTIONS.some((o) => o.value === sort)
      ? (sort as SortValue)
      : undefined,
    q: params.get("q") || undefined,
    page: Math.max(1, Number(params.get("page")) || 1),
  };
}

type ListState = {
  /** The last page that arrived: kept on screen while the next loads. */
  list: ListResponse<CustomerDocumentItem> | null;
  isLoading: boolean;
  error: string | null;
};

const documentPath = (row: CustomerDocumentItem): string =>
  row.kind === "invoice"
    ? paths.invoiceDetail({ invoiceNo: row.document_no })
    : row.kind === "proforma"
      ? paths.proformaDetail({ proformaNo: row.document_no })
      : paths.creditNoteDetail({ creditNoteNo: row.document_no });

const KIND_LABELS: Record<CustomerDocumentKind, string> = {
  proforma: "Pro-forma",
  invoice: "Invoice",
  credit_note: "Credit note",
};

export function CustomerInvoicesPage() {
  const customerId = useCustomerRouteId();
  if (customerId === null) return null;
  return (
    <CustomerFrame customerId={customerId} current="invoices">
      <CustomerInvoices customerCode={customerId} />
    </CustomerFrame>
  );
}

function CustomerInvoices({ customerCode }: { customerCode: string }) {
  const adapter = useDataAdapter();
  const [params, setParams] = useSearchParams();
  const { type, state, sort, q, page } = readFilters(params);
  const isFiltered = Boolean(type || state || q);

  const setFilter = (key: string, value: string | null) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        return next;
      },
      { replace: true },
    );

  // The search box is typed into freely and written to the URL once it settles.
  const [query, setQuery] = useState(q ?? "");
  const debouncedQuery = useDebouncedValue(query, 300).trim();
  useEffect(() => {
    if (debouncedQuery !== (q ?? "")) setFilter("q", debouncedQuery || null);
    // The URL's own q is the other direction; only a settled box writes it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const [listState, setListState] = useState<ListState>({
    list: null,
    isLoading: true,
    error: null,
  });
  useEffect(() => {
    let cancelled = false;
    setListState((s) => ({ ...s, isLoading: true, error: null }));
    adapter
      .listCustomerDocuments(customerCode, {
        type: type ? [type] : undefined,
        state: state ? [state] : undefined,
        q,
        sort,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((list) => {
        if (!cancelled) setListState({ list, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setListState((s) => ({
            ...s,
            isLoading: false,
            error:
              err instanceof Error
                ? err.message
                : "The documents did not load.",
          }));
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, customerCode, type, state, q, sort, page]);

  const pdf = usePdfDownload();
  const viewPdf = (row: CustomerDocumentItem) => void pdf.viewPdf(row.pdf_url);

  const { list } = listState;
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground max-w-2xl text-sm">
        The pro-formas, invoices and credit notes One has raised for this
        customer. Credit notes are shown as negatives. Invoices raised in
        Tharstern are not listed here.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Document or order number…"
          aria-label="Search this customer's documents"
          className="h-9 w-full sm:w-72"
        />
        <select
          aria-label="Type of document"
          value={type ?? ""}
          onChange={(e) => setFilter("type", e.target.value || null)}
          className={selectClass}
        >
          <option value="">All documents</option>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Where it stands"
          value={state ?? ""}
          onChange={(e) => setFilter("state", e.target.value || null)}
          className={selectClass}
        >
          <option value="">Any state</option>
          {STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Sort documents"
          value={sort ?? ""}
          onChange={(e) => setFilter("sort", e.target.value || null)}
          className={cn(selectClass, "sm:ml-auto")}
        >
          <option value="">Sort…</option>
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {pdf.notice && (
        <p role="status" className="text-muted-foreground text-sm">
          {pdf.notice}
        </p>
      )}
      {listState.error && (
        <p role="alert" className="text-sm text-red-600">
          Could not load the documents: {listState.error}
        </p>
      )}
      {!list && listState.isLoading && (
        <div className="space-y-2" aria-label="Loading the documents">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}
      {list &&
        (list.items.length === 0 ? (
          <div className="text-muted-foreground py-6 text-sm">
            {isFiltered
              ? "No documents match."
              : "One has raised no documents for this customer yet."}
          </div>
        ) : (
          <div
            aria-busy={listState.isLoading}
            className={cn(
              "transition-opacity",
              listState.isLoading && "opacity-60",
            )}
          >
            <p className="text-muted-foreground mb-2 text-sm tabular-nums">
              {list.total.toLocaleString("en-GB")}{" "}
              {list.total === 1 ? "document" : "documents"}
              {isFiltered ? " match" : ""}
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead className="hidden md:table-cell">Order</TableHead>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                  <TableHead className="hidden text-right lg:table-cell">
                    Net
                  </TableHead>
                  <TableHead className="hidden text-right lg:table-cell">
                    VAT
                  </TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead>Where it stands</TableHead>
                  <TableHead>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.items.map((row) => (
                  <TableRow key={row.document_no}>
                    <TableCell className="font-medium">
                      <Link to={documentPath(row)} className="hover:underline">
                        {row.document_no}
                      </Link>
                      <div className="text-muted-foreground text-xs font-normal">
                        {KIND_LABELS[row.kind]}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {row.order_no === null ? (
                        <span className="text-muted-foreground">
                          Period charges
                        </span>
                      ) : (
                        <Link
                          to={paths.orderDetail({ orderNo: row.order_no })}
                          className="hover:underline"
                        >
                          {row.order_no}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell className="hidden tabular-nums md:table-cell">
                      {formatShortDateOrDash(row.issued_on)}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums lg:table-cell">
                      {formatQuotedPrice(row.net_pence)}
                    </TableCell>
                    <TableCell className="hidden text-right tabular-nums lg:table-cell">
                      {formatQuotedPrice(row.vat_pence)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatQuotedPrice(row.gross_pence)}
                    </TableCell>
                    <TableCell>
                      <StateBadge state={row.state} label={row.state_label} />
                      {row.is_disputed && (
                        <>
                          {" "}
                          <Badge variant="warning" className="font-normal">
                            Disputed
                          </Badge>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={pdf.busy}
                        onClick={() => viewPdf(row)}
                        aria-label={`View PDF of ${row.document_no}`}
                      >
                        View PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="py-3">
              <ListPagination
                page={list.page}
                totalPages={list.totalPages}
                onPageChange={(next) =>
                  setFilter("page", next > 1 ? String(next) : null)
                }
              />
            </div>
          </div>
        ))}
    </div>
  );
}
